import { OrderDetails } from 'restaurantAdmin/components/orderDetails/OrderDetails';
import { ReservationGuestSheetReceipt } from 'restaurantAdmin/components/reservationGuestSheetReceipt/ReservationGuestSheetReceipt';
import { AssignedServer } from 'restaurantAdmin/components/server/AssignedServer';
import { isReservationFinished } from 'restaurantAdmin/reservations/reservationUtils';
import { SnapshotFloorPlan } from '@components/floorPlan/SnapshotFloorPlan';
import { Icon } from '@components/icon/Icon';
import { useHandleClickOutside } from '@shared/hooks/useHandleClickOutside';
import { parseToShortDateFormat } from '@shared/utils/dateFormatters';
import { appendGuestCountLabel } from '@utils/guestCount';
import { ISOTimeTo12HourTime } from '@utils/time';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { RefundButton } from '../components/refundButton/RefundButton';
import { RemediationForm } from '../components/remediationForm/RemediationForm';
import type { GuestReservation } from './apiHelpers';
import { GuestReservationCancellationFee } from './GuestReservationCancellationFee';
import styles from './GuestReservationDetailsDrawer.scss';

interface GuestReservationDetailsDrawerProps {
  guestReservation: GuestReservation;
  onClose: () => void;
}

export const GuestReservationDetailsDrawer = ({
  onClose,
  guestReservation,
}: GuestReservationDetailsDrawerProps) => {
  const { ref } = useHandleClickOutside(onClose);
  const { transactionDetails } = guestReservation;
  const { refundAmount } = transactionDetails;
  const isRefunded = !!refundAmount;
  const tableName =
    guestReservation.seatedTableName &&
    isReservationFinished(guestReservation.status)
      ? `(${guestReservation.seatedTableName})`
      : '';

  return (
    <div
      aria-label="reservation details"
      className={styles.drawer}
      ref={ref}
      role="dialog"
    >
      <button
        aria-label="close reservation details"
        className={styles.closeButton}
        onClick={onClose}
      >
        <Icon name="close" />
      </button>
      <section className={styles.floorPlanSection}>
        <hgroup>
          <h3 className={typography.h6}>
            {`${parseToShortDateFormat(guestReservation.date)}, ${ISOTimeTo12HourTime(
              guestReservation.time,
            )}`}
          </h3>
          <p className={cx(typography.h6, styles.capitalize)}>
            {`${guestReservation.status} ${tableName}`}
          </p>
        </hgroup>
        <div className={typography.c3}>
          <p className={styles.capitalize}>{guestReservation.publicName}</p>
          <p>{appendGuestCountLabel(guestReservation.guestCount)}</p>
        </div>
        <SnapshotFloorPlan reservationId={guestReservation.id} />
      </section>
      <ReservationGuestSheetReceipt
        className={styles.receiptSection}
        transactionDetails={transactionDetails}
      />
      {guestReservation.cancellationFeeTransaction && (
        <GuestReservationCancellationFee
          cancellationFee={guestReservation.cancellationFeeTransaction}
          className={styles.receiptSection}
        />
      )}
      <OrderDetails
        order={guestReservation.lightspeedOrder}
        className={styles.receiptSection}
      />
      {transactionDetails.createdBy.fullName && (
        <section className={styles.bookedBySection}>
          <h3 className={typography.c3}>Booked By</h3>
          <p className={typography.c3}>
            {transactionDetails.createdBy.fullName}
          </p>
        </section>
      )}
      {isReservationFinished(guestReservation.status) && (
        <AssignedServer
          className={styles.serverSection}
          emptyStateText="No server was recorded for this reservation"
          isEditable={false}
          occupant={{
            occupantId: guestReservation.id,
            occupantType: 'reservation',
          }}
          server={guestReservation.server}
        />
      )}
      <section className={styles.staffNoteSection}>
        <h3 className={typography.c3}>Visit Notes</h3>
        <p className={typography.c3}>{guestReservation.staffNote || 'N/A'}</p>
      </section>
      <RemediationForm
        disableConfirmationModalPortal
        reservationId={guestReservation.id}
        reservationStatus={guestReservation.status}
      />
      <RefundButton
        reservationId={guestReservation.id}
        reservationPrice={transactionDetails.price}
        isRefunded={isRefunded}
      />
    </div>
  );
};
